// client/src/pages/Categories.js

import React, { useState, useEffect } from 'react';
import Table from 'react-bootstrap/Table';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import Badge from 'react-bootstrap/Badge';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { FaMagic, FaPen, FaTrash, FaPlus } from 'react-icons/fa';
import useCategories from '../hooks/useCategories';
import { orderCategoriesHierarchically } from '../utils/categoryTree';

const TYPE_LABELS = {
  EXPENSE: { label: 'Dépense', variant: 'danger' },
  INCOME: { label: 'Revenu', variant: 'success' },
  TRANSFER: { label: 'Virement', variant: 'secondary' }
};

const EMPTY_FORM = { name: '', color: '#888780', type: 'EXPENSE', monthlyBudget: '', parentId: '' };

function Categories() {
  const {
    categories,
    loading,
    error,
    fetchCategories,
    createCategory,
    updateCategory,
    deleteCategory,
    categorize
  } = useCategories();

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [catResult, setCatResult] = useState(null);

  useEffect(() => {
    fetchCategories().catch(() => {});
  }, [fetchCategories]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (cat) => {
    setEditingId(cat.id);
    setForm({
      name: cat.name,
      color: cat.color,
      type: cat.type,
      monthlyBudget: cat.monthlyBudget ?? '',
      parentId: cat.parentId ?? ''
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    const payload = {
      name: form.name,
      color: form.color,
      type: form.type,
      monthlyBudget: form.monthlyBudget === '' ? null : Number(form.monthlyBudget),
      parentId: form.parentId || null
    };
    try {
      if (editingId) {
        await updateCategory(editingId, payload);
      } else {
        await createCategory(payload);
      }
      setShowModal(false);
      await fetchCategories();
    } catch (err) {
      // Erreur affichée via le hook
    }
  };

  const handleDelete = async (cat) => {
    if (!window.confirm(`Supprimer la catégorie « ${cat.name} » ?`)) return;
    try {
      await deleteCategory(cat.id);
      await fetchCategories();
    } catch (err) {
      // Erreur affichée via le hook
    }
  };

  const handleCategorize = async () => {
    setCatResult(null);
    try {
      const stats = await categorize();
      setCatResult(stats);
      await fetchCategories();
    } catch (err) {
      // Erreur affichée via le hook
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <h1 className="mb-0">Catégories</h1>
        <div className="d-flex gap-2">
          <Button variant="outline-primary" onClick={openCreate}>
            <FaPlus className="me-2" />
            Nouvelle catégorie
          </Button>
          <Button variant="primary" onClick={handleCategorize} disabled={loading.categorize}>
            {loading.categorize ? (
              <Spinner as="span" animation="border" size="sm" className="me-2" />
            ) : (
              <FaMagic className="me-2" />
            )}
            Catégoriser mes transactions
          </Button>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {catResult && (
        <Alert variant="success" onClose={() => setCatResult(null)} dismissible>
          {catResult.total === 0
            ? 'Toutes vos transactions sont déjà catégorisées.'
            : `${catResult.viaCache + catResult.viaAi} transaction(s) catégorisée(s) ` +
              `(${catResult.viaCache} via le cache, ${catResult.viaAi} via l'IA).` +
              (catResult.uncategorized > 0
                ? ` ${catResult.uncategorized} non catégorisée(s).`
                : '')}
        </Alert>
      )}

      {loading.categories ? (
        <Spinner animation="border" />
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Catégorie</th>
              <th>Type</th>
              <th className="text-end">Budget mensuel</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {orderCategoriesHierarchically(categories).map((cat) => {
              const typeInfo = TYPE_LABELS[cat.type] || TYPE_LABELS.EXPENSE;
              return (
                <tr key={cat.id}>
                  <td style={{ paddingLeft: cat.depth ? 32 : undefined }}>
                    {cat.depth ? <span className="text-muted me-1">↳</span> : null}
                    <span
                      className="d-inline-block rounded-circle me-2"
                      style={{
                        width: 14,
                        height: 14,
                        backgroundColor: cat.color,
                        verticalAlign: 'middle'
                      }}
                    />
                    {cat.name}
                    {cat.isSystem && (
                      <Badge bg="light" text="dark" className="ms-2">
                        système
                      </Badge>
                    )}
                  </td>
                  <td>
                    <Badge bg={typeInfo.variant}>{typeInfo.label}</Badge>
                  </td>
                  <td className="text-end">
                    {cat.monthlyBudget != null ? `${Number(cat.monthlyBudget).toFixed(2)} €` : '—'}
                  </td>
                  <td className="text-end">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="me-2"
                      onClick={() => openEdit(cat)}
                    >
                      <FaPen />
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDelete(cat)}
                      disabled={cat.isSystem}
                    >
                      <FaTrash />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{editingId ? 'Modifier la catégorie' : 'Nouvelle catégorie'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Nom</Form.Label>
              <Form.Control
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Form.Group>
            <Row>
              <Col xs={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Couleur</Form.Label>
                  <Form.Control
                    type="color"
                    value={form.color}
                    onChange={(e) => setForm({ ...form, color: e.target.value })}
                    title="Choisir une couleur"
                  />
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Type</Form.Label>
                  <Form.Select
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                  >
                    <option value="EXPENSE">Dépense</option>
                    <option value="INCOME">Revenu</option>
                    <option value="TRANSFER">Virement</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Catégorie parente (optionnel)</Form.Label>
              <Form.Select
                value={form.parentId}
                onChange={(e) => setForm({ ...form, parentId: e.target.value })}
              >
                <option value="">— Aucune (catégorie principale) —</option>
                {categories
                  .filter((c) => !c.parentId && c.id !== editingId)
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
              </Form.Select>
              <Form.Text className="text-muted">
                Pour créer une sous-catégorie (ex : Vacances → Chine 2025).
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Budget mensuel (optionnel)</Form.Label>
              <Form.Control
                type="number"
                min="0"
                step="0.01"
                placeholder="Aucun budget"
                value={form.monthlyBudget}
                onChange={(e) => setForm({ ...form, monthlyBudget: e.target.value })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Annuler
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={loading.save || !form.name}>
            {loading.save ? <Spinner as="span" animation="border" size="sm" /> : 'Enregistrer'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default Categories;
